import { Form } from 'antd'
import type { FormInstance } from 'antd/lib/form/Form'
import moment from 'moment'
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import useMediaQuery from 'use-media-antd-query'

import { useOrders } from '../../../../../../api/useOrders'
import type { ReducerAction } from './index.types'

type FormState = {
  tractor: string
  driver: string
  delivery_date: Date
  attendant: string
  attendant_notes: string
  movements: Gravitate.OrderMovement[]
}

const initialState = {
  activeOrder: null as Gravitate.Order,
  metadata: {
    products: [],
  },
  attendant_notes: '',
  movements: [],
  isLoading: false,
  isDrawerOpen: false,
}

type State = Omit<typeof initialState, 'movements'> & {
  movements: Gravitate.OrderMovement[]
  activeOrder: Gravitate.Order
}

type ContextValue = State & {
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
  setActiveOrder: (order: Gravitate.Order) => void
  form: FormInstance<FormState>
  isOrderReadOnly: boolean
  isPotatoMode: boolean
}

const Context = createContext<ContextValue>(null)

function reducer(state: State, action: ReducerAction) {
  switch (action.type) {
    case 'SET_DRAWER_VISIBILITY':
      return { ...state, isDrawerOpen: action.payload, activeOrder: null }
    case 'SET_ACTIVE_ORDER':
      return { ...state, activeOrder: action.payload, isDrawerOpen: true }
    default:
      return state
  }
}

export function MovementStoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [form] = Form.useForm<FormState>()

  const isOrderReadOnly = useMemo(
    () => ['completed', 'missing_price'].includes(state.activeOrder?.movement_status),
    [state.activeOrder]
  )

  const screenSize = useMediaQuery()
  const isPotatoMode = ['xs', 'sm', 'md', 'lg'].includes(screenSize)

  const initializeFormState = () => {
    if (isOrderReadOnly) {
      form.setFieldsValue({
        tractor: state?.activeOrder?.tractor ?? '',
        driver: state?.activeOrder?.driver ?? '',
        attendant: state?.activeOrder?.attendant_notes?.updated_by ?? '',
        attendant_notes: state?.activeOrder?.attendant_notes ?? '',
        delivery_date: moment(state?.activeOrder?.actual_end ?? state?.activeOrder?.carrier_window_start),
        movements: state?.activeOrder?.bols?.map((bol) => ({
          contract: bol?.contract,
          number: bol?.bol_number || '',
          date: moment(bol?.bol_date ?? state?.activeOrder?.actual_end ?? state?.activeOrder?.carrier_window_start),
          bol_product: bol?.product,
          drop_product: bol?.tank_product ?? bol?.product,
          supplier: bol?.supplier,
          terminal: bol?.terminal,
          gross_quantity: bol?.gross_volume,
          net_quantity: bol?.net_volume,
        })),
      })
      return
    }

    form.setFieldsValue({
      tractor: '',
      driver: '',
      attendant_notes: '',
      delivery_date: moment(state?.activeOrder?.actual_end ?? state?.activeOrder?.carrier_window_start),
      movements: state?.activeOrder?.component_paths?.map((c) => ({
        contract: c?.contract,
        number: '',
        date: null,
        bol_product: c?.component_product_name,
        drop_product: c?.product_name,
        supplier: c?.supplier,
        terminal: c?.terminal,
      })),
    })
  }

  useEffect(initializeFormState, [state.activeOrder])

  const { useBackhaulMetadataQuery } = useOrders()
  const {
    data: { products } = { products: [] },
  } = useBackhaulMetadataQuery()

  const openDrawer = () => dispatch({ type: 'SET_DRAWER_VISIBILITY', payload: true })
  const closeDrawer = () => dispatch({ type: 'SET_DRAWER_VISIBILITY', payload: false })
  const toggleDrawer = () => dispatch({ type: 'SET_DRAWER_VISIBILITY', payload: !state.isDrawerOpen })
  const setActiveOrder = (order: Gravitate.Order) => dispatch({ type: 'SET_ACTIVE_ORDER', payload: order })

  return (
    <Context.Provider
      value={{
        ...state,
        form,
        metadata: { products },
        openDrawer,
        closeDrawer,
        toggleDrawer,
        setActiveOrder,
        isOrderReadOnly,
        isPotatoMode,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useEasyOrderMovements = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useEasyOrderMovements must be used within a MovementStoreProvider')
  }
  return context
}
